import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import api from '@/lib/api';
import { toast } from 'sonner';
import { 
  Sparkles, 
  Lightbulb, 
  TrendingUp, 
  Gift, 
  MessageSquare,
  Loader2,
  CheckCircle,
  Copy,
  Lock,
  Crown
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const AIAssistant = () => {
  const [dishes, setDishes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restaurant, setRestaurant] = useState(null);
  
  // AI results
  const [descriptionResult, setDescriptionResult] = useState('');
  const [highlightResult, setHighlightResult] = useState(null);
  const [upsellResult, setUpsellResult] = useState('');
  const [promotionResult, setPromotionResult] = useState('');
  
  // Loading states
  const [descLoading, setDescLoading] = useState(false);
  const [highlightLoading, setHighlightLoading] = useState(false);
  const [upsellLoading, setUpsellLoading] = useState(false);
  const [promoLoading, setPromoLoading] = useState(false);
  
  // Form states
  const [descForm, setDescForm] = useState({ name: '', category: '' });
  const [upsellDish, setUpsellDish] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [dishRes, catRes, restRes] = await Promise.all([
        api.get('/dishes'),
        api.get('/categories'),
        api.get('/restaurant')
      ]);
      setDishes(dishRes.data);
      setCategories(catRes.data);
      setRestaurant(restRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const generateDescription = async () => {
    if (!descForm.name.trim()) {
      toast.error('Entrez le nom du plat');
      return;
    }
    setDescLoading(true);
    try {
      const response = await api.post('/ai/generate-description', {
        dish_name: descForm.name,
        dish_category: descForm.category || 'Plat'
      });
      setDescriptionResult(response.data.description);
      toast.success('Description générée !');
    } catch (error) {
      toast.error('Erreur lors de la génération');
    } finally {
      setDescLoading(false);
    }
  };

  const suggestHighlight = async () => {
    setHighlightLoading(true);
    try {
      const response = await api.post('/ai/suggest-highlight');
      setHighlightResult(response.data);
      toast.success('Suggestion reçue !');
    } catch (error) {
      toast.error('Erreur lors de la suggestion');
    } finally {
      setHighlightLoading(false);
    }
  };

  const suggestUpsells = async () => {
    if (!upsellDish) {
      toast.error('Sélectionnez un plat');
      return;
    }
    setUpsellLoading(true);
    try {
      const response = await api.post('/ai/suggest-upsells', { dish_id: upsellDish });
      setUpsellResult(response.data.upsells);
      toast.success('Suggestions reçues !');
    } catch (error) {
      toast.error('Erreur lors de la suggestion');
    } finally {
      setUpsellLoading(false);
    }
  };

  const suggestPromotion = async () => {
    setPromoLoading(true);
    try {
      const response = await api.post('/ai/suggest-promotion');
      setPromotionResult(response.data.promotion);
      toast.success('Promotion suggérée !');
    } catch (error) {
      toast.error('Erreur lors de la suggestion');
    } finally {
      setPromoLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié !');
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid md:grid-cols-2 gap-6">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-64" />)}
        </div>
      </div>
    );
  }

  // Check if user has access (Essentiel+ only)
  const hasAccess = restaurant?.subscription_plan !== 'starter';

  if (!hasAccess) {
    return (
      <div className="space-y-6" data-testid="ai-assistant-page">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Sparkles className="h-8 w-8 text-orange-500" />
            Assistant IA
            <Badge className="bg-amber-100 text-amber-700">Essentiel+</Badge>
          </h1>
          <p className="text-gray-500 mt-1">
            Utilisez l'intelligence artificielle pour optimiser vos ventes
          </p>
        </div>

        <Card className="border-0 shadow-soft">
          <CardContent className="p-8 text-center">
            <Lock className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h2 className="font-heading text-xl font-bold text-gray-900 mb-2">
              Fonctionnalité Essentiel / Premium
            </h2>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Accédez à l'assistant IA pour générer des descriptions, 
              suggérer des plats du jour et créer des promotions automatiquement.
            </p>
            <div className="grid sm:grid-cols-2 gap-4 max-w-md mx-auto mb-6">
              <div className="p-4 bg-gray-50 rounded-xl text-left">
                <MessageSquare className="h-6 w-6 text-blue-500 mb-2" />
                <p className="font-medium text-gray-900 text-sm">Descriptions IA</p>
                <p className="text-xs text-gray-500">Textes appétissants générés</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-xl text-left">
                <Lightbulb className="h-6 w-6 text-amber-500 mb-2" />
                <p className="font-medium text-gray-900 text-sm">Plat du jour</p>
                <p className="text-xs text-gray-500">Suggestions quotidiennes</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-xl text-left">
                <TrendingUp className="h-6 w-6 text-emerald-500 mb-2" />
                <p className="font-medium text-gray-900 text-sm">Upsells</p>
                <p className="text-xs text-gray-500">Recommandations de ventes</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-xl text-left">
                <Gift className="h-6 w-6 text-pink-500 mb-2" />
                <p className="font-medium text-gray-900 text-sm">Promotions</p>
                <p className="text-xs text-gray-500">Création d'offres optimisées</p>
              </div>
            </div>
            <Button
              onClick={() => window.location.href = '/billing'}
              className="bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600"
            >
              <Crown className="h-4 w-4 mr-2" />
              Passer à Essentiel
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ai-assistant-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
          <Sparkles className="h-8 w-8 text-orange-500" />
          Assistant IA
        </h1>
        <p className="text-gray-500 mt-1">
          Utilisez l'intelligence artificielle pour optimiser vos ventes
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Generate Description */}
        <Card className="border-0 shadow-soft">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-blue-500" />
              Générer une description
            </CardTitle>
            <CardDescription>
              Créez des descriptions appétissantes pour vos plats
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Nom du plat</Label>
              <Input
                value={descForm.name}
                onChange={(e) => setDescForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ex: Burger Gourmet"
                data-testid="ai-dish-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Catégorie</Label>
              <Select 
                value={descForm.category} 
                onValueChange={(value) => setDescForm(prev => ({ ...prev, category: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner une catégorie" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={generateDescription}
              disabled={descLoading}
              className="w-full bg-blue-500 hover:bg-blue-600 text-white rounded-full"
              data-testid="generate-description-btn"
            >
              {descLoading ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Génération...</>
              ) : (
                <><Sparkles className="h-4 w-4 mr-2" /> Générer</>
              )}
            </Button>
            {descriptionResult && (
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-100 relative">
                <p className="text-gray-700 pr-8">{descriptionResult}</p>
                <button 
                  onClick={() => copyToClipboard(descriptionResult)}
                  className="absolute top-3 right-3 p-1.5 text-gray-400 hover:text-gray-600"
                >
                  <Copy className="h-4 w-4" />
                </button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Suggest Highlight */}
        <Card className="border-0 shadow-soft">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-yellow-500" />
              Plat à mettre en avant
            </CardTitle>
            <CardDescription>
              L'IA analyse votre menu et suggère le meilleur plat du jour
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              onClick={suggestHighlight}
              disabled={highlightLoading || dishes.length === 0}
              className="w-full bg-yellow-500 hover:bg-yellow-600 text-white rounded-full"
              data-testid="suggest-highlight-btn"
            >
              {highlightLoading ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Analyse...</>
              ) : (
                <><Lightbulb className="h-4 w-4 mr-2" /> Obtenir la suggestion</>
              )}
            </Button>
            {highlightResult && (
              <div className="p-4 bg-yellow-50 rounded-xl border border-yellow-100">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-gray-700">{highlightResult.suggestion}</p>
                  </div>
                </div>
              </div>
            )}
            {dishes.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-2">
                Ajoutez des plats à votre menu pour activer cette fonctionnalité
              </p>
            )}
          </CardContent>
        </Card>

        {/* Suggest Upsells */}
        <Card className="border-0 shadow-soft">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-emerald-500" />
              Suggestions d'upsells
            </CardTitle>
            <CardDescription>
              Recommandations pour augmenter le panier moyen
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Plat commandé</Label>
              <Select value={upsellDish} onValueChange={setUpsellDish}>
                <SelectTrigger data-testid="upsell-dish-select">
                  <SelectValue placeholder="Sélectionner un plat" />
                </SelectTrigger>
                <SelectContent>
                  {dishes.map((dish) => (
                    <SelectItem key={dish.id} value={dish.id}>
                      {dish.name} - {dish.price.toFixed(2)}€
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={suggestUpsells}
              disabled={upsellLoading || !upsellDish}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-full"
              data-testid="suggest-upsells-btn"
            >
              {upsellLoading ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Analyse...</>
              ) : (
                <><TrendingUp className="h-4 w-4 mr-2" /> Suggérer des accompagnements</>
              )}
            </Button>
            {upsellResult && (
              <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100 relative">
                <p className="text-gray-700 whitespace-pre-line pr-8">{upsellResult}</p>
                <button 
                  onClick={() => copyToClipboard(upsellResult)}
                  className="absolute top-3 right-3 p-1.5 text-gray-400 hover:text-gray-600"
                >
                  <Copy className="h-4 w-4" />
                </button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Suggest Promotion */}
        <Card className="border-0 shadow-soft">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Gift className="h-5 w-5 text-pink-500" />
              Créer une promotion
            </CardTitle>
            <CardDescription>
              L'IA crée une promotion attractive basée sur votre menu
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              onClick={suggestPromotion}
              disabled={promoLoading || dishes.length === 0}
              className="w-full bg-pink-500 hover:bg-pink-600 text-white rounded-full"
              data-testid="suggest-promotion-btn"
            >
              {promoLoading ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Création...</>
              ) : (
                <><Gift className="h-4 w-4 mr-2" /> Créer une promotion</>
              )}
            </Button>
            {promotionResult && (
              <div className="p-4 bg-pink-50 rounded-xl border border-pink-100 relative">
                <p className="text-gray-700 whitespace-pre-line pr-8">{promotionResult}</p>
                <button 
                  onClick={() => copyToClipboard(promotionResult)}
                  className="absolute top-3 right-3 p-1.5 text-gray-400 hover:text-gray-600"
                >
                  <Copy className="h-4 w-4" />
                </button>
              </div>
            )}
            {dishes.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-2">
                Ajoutez des plats à votre menu pour activer cette fonctionnalité
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tips */}
      <Card className="border-0 shadow-soft bg-gradient-to-br from-orange-50 to-white">
        <CardContent className="p-6">
          <h3 className="font-heading font-semibold text-lg text-gray-900 mb-3">
            💡 Conseils pour de meilleurs résultats
          </h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li>• Ajoutez des marges sur vos plats pour que l'IA recommande les plus rentables</li>
            <li>• Plus vous avez de plats, plus les suggestions seront pertinentes</li>
            <li>• Utilisez les descriptions générées comme base et personnalisez-les</li>
            <li>• Testez régulièrement les suggestions de mise en avant</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default AIAssistant;
